import React, { useState, useEffect, useRef } from 'react';
import { Activity, Zap, ChevronDown } from 'lucide-react';
import { useOptionChainStore } from '../../../store/optionChainStore';
import { useSyncOptionChainStore } from '../../../store/syncOptionChainStore';
import server from '../../../utils/serverConfig';
import apiClient from '../../../utils/apiClient';
import { encryptApiKey } from '../../../utils/cryptoUtils';

const STRIKE_STEPS = {
    GOLD: { MCX: 1000, COMEX: 10 },
    SILVER: { MCX: 1000, COMEX: 0.5 },
    CRUDEOIL: { MCX: 50, COMEX: 0.5 },
    NATURALGAS: { MCX: 5, COMEX: 0.05 }
};

// Conversion Ratios for INR-equivalent pricing (used for % diff alert threshold)
const CONVERSION_RATIOS = {
    GOLD: { comex: 1, mcx: 3 },
    SILVER: { comex: 1, mcx: 5 },
    NATURALGAS: { comex: 1, mcx: 8 },
    CRUDEOIL: { comex: 1, mcx: 10 },
};

// Lot sizes for absolute INR delta calculation
// COMEX_delta_INR = LTP × COMEX_lot × ratio.comex × USDINR
// MCX_delta_INR   = LTP × MCX_lot   × ratio.mcx
const LOT_SIZES = {
    GOLD: { comex: 100, mcx: 100 },
    SILVER: { comex: 5000, mcx: 30 },
    CRUDEOIL: { comex: 1000, mcx: 100 },
    NATURALGAS: { comex: 10000, mcx: 1250 },
};

const SOCKET_URL = "wss://godseye.ws.server.sypto.xyz/ws/global_ltp";

const ArbitrageView = ({ activeCommodityTab, setActiveCommodityTab }) => {
    const chainData = useOptionChainStore(state => state.chainData);
    const futuresData = useOptionChainStore(state => state.futuresData);
    const updateOptionChain = useOptionChainStore(state => state.updateOptionChain);
    const deleteOption = useOptionChainStore(state => state.deleteOption);
    const updateFutures = useOptionChainStore(state => state.updateFutures);
    const setStatus = useOptionChainStore(state => state.setStatus);
    const status = useOptionChainStore(state => state.status);

    // Feed is_sync ticks to the sync store (so SyncArbitrageView doesn't need its own WS)
    const syncUpdateOptionChain = useSyncOptionChainStore(state => state.updateOptionChain);
    const syncDeleteOption = useSyncOptionChainStore(state => state.deleteOption);
    const syncUpdateFutures = useSyncOptionChainStore(state => state.updateFutures);

    const ws = useRef(null);
    const isMounted = useRef(true);

    const [expiries, setExpiries] = useState({
        MCX: { GOLD: [], SILVER: [], CRUDEOIL: [], NATURALGAS: [] },
        COMEX: { GOLD: [], SILVER: [], CRUDEOIL: [], NATURALGAS: [] },
        MCX_FUTURES: { GOLD: [], SILVER: [], CRUDEOIL: [], NATURALGAS: [] },
        COMEX_FUTURES: { GOLD: [], SILVER: [], CRUDEOIL: [], NATURALGAS: [] }
    });
    const [globalAlerts, setGlobalAlerts] = useState([]);
    const [usdinrRate, setUsdinrRate] = useState(null);
    const [selectedExpiries, setSelectedExpiries] = useState({
        MCX: { GOLD: null, SILVER: null, CRUDEOIL: null, NATURALGAS: null },
        COMEX: { GOLD: null, SILVER: null, CRUDEOIL: null, NATURALGAS: null }
    });
    const [isAlertsExpanded, setIsAlertsExpanded] = useState(true);
    const [futuresDiffMode, setFuturesDiffMode] = useState('pct'); // 'pct' or 'inr'

    useEffect(() => {
        // Fetch expiries for all commodities
        const fetchExpiries = async () => {
            try {
                const commodities = ['GOLD', 'SILVER', 'CRUDEOIL', 'NATURALGAS'];
                const newExpiries = { MCX: {}, COMEX: {}, MCX_FUTURES: {}, COMEX_FUTURES: {} };
                const newSelected = { MCX: {}, COMEX: {} };

                // Safety net: filter out dates that are already in the past (before today IST)
                const filterFutureExpiries = (list) => {
                    const todayIST = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
                    todayIST.setHours(0, 0, 0, 0);
                    return (list || []).filter(dateStr => {
                        const d = new Date(dateStr);
                        return !isNaN(d) && d >= todayIST;
                    });
                };

                for (const commodity of commodities) {
                    const res = await apiClient.get(`${server.godseye.baseUrl}api/expiries?underlying=${commodity}`);
                    const data = res.data;

                    newExpiries.MCX[commodity] = filterFutureExpiries(data.MCX);
                    newExpiries.COMEX[commodity] = filterFutureExpiries(data.COMEX);
                    newExpiries.MCX_FUTURES[commodity] = filterFutureExpiries(data.MCX_FUTURES || data.MCX);
                    newExpiries.COMEX_FUTURES[commodity] = filterFutureExpiries(data.COMEX_FUTURES || data.COMEX);

                    // Set first expiry as default
                    if (data.MCX && data.MCX.length > 0) {
                        newSelected.MCX[commodity] = data.MCX[0];
                    }
                    if (data.COMEX && data.COMEX.length > 0) {
                        newSelected.COMEX[commodity] = data.COMEX[0];
                    }
                }

                setExpiries(newExpiries);
                setSelectedExpiries(newSelected);
            } catch (error) {
                console.error("Failed to fetch expiries:", error);
            }
        };

        fetchExpiries();
        connectWebSocket();
        return () => {
            isMounted.current = false;
            if (ws.current) ws.current.close();
        };
    }, []);

    const connectWebSocket = () => {
        const apiKey = process.env.REACT_APP_API_KEY || "godseye_frontend_key_123";
        const secretKey = process.env.REACT_APP_SECRET_KEY || "godseye_secret_key_456";
        const token = encryptApiKey(apiKey, secretKey);
        
        ws.current = new WebSocket(`${SOCKET_URL}?token=${token}`);

        ws.current.onopen = () => {
            setStatus("connected");
        };

        ws.current.onmessage = (event) => {
            try {
                const rawMessage = JSON.parse(event.data);

                const processMessage = (message) => {
                    if (message.op === "DELETE") {
                        if (message.is_sync) {
                            syncDeleteOption(message);
                        } else {
                            deleteOption(message);
                        }
                        return;
                    }

                    // Handle "wrapped" messages
                    if (message.type === "option_chain_update") {
                        updateOptionChain(message.data);
                    }
                    else if (message.type === "futures_update") {
                        updateFutures(message.data);
                        syncUpdateFutures(message.data);
                    }
                    // Handle RAW ticks (direct from producers)
                    else if (message.strike && (message.type === "CE" || message.type === "PE")) {
                        if (message.is_sync) {
                            // Route sync ticks to sync store (consumed by SyncArbitrageView)
                            syncUpdateOptionChain(message);
                        } else {
                            updateOptionChain(message);
                        }
                    }
                    else if (message.type === "FUTURE") {
                        updateFutures(message);
                        syncUpdateFutures(message);
                    }
                    else if (message.type === "USDINR") {
                        setUsdinrRate(message.ltp);
                    }
                };

                if (Array.isArray(rawMessage)) {
                    rawMessage.forEach(processMessage);
                } else {
                    processMessage(rawMessage);
                }
            } catch (err) {
                console.error("Error parsing WS message:", err);
            }
        };

        ws.current.onclose = () => {
            if (isMounted.current) {
                setStatus("disconnected");
                setTimeout(connectWebSocket, 3000);
            }
        };
    };

    const getPercent = (value, total) => {
        if (!total || total === 0) return "0.00";
        return ((value / total) * 100).toFixed(2);
    };

    const calculateStraddle = (ceLtp, peLtp, strike) => {
        if (!ceLtp || !peLtp || !strike) return "-";
        return getPercent(ceLtp + peLtp, strike);
    };

    const getAtmStrike = (ltp, step) => {
        if (!ltp) return null;
        return Math.round(ltp / step) * step;
    };

    const formatLtt = (timestamp) => {
        if (!timestamp) return "-";
        try {
            return new Date(timestamp * 1000).toLocaleString("en-GB", {
                timeZone: "Asia/Kolkata",
                day: '2-digit', month: 'short', hour12: false, hour: '2-digit', minute: '2-digit'
            });
        } catch (e) {
            return "-";
        }
    };

    const selectedMcxExpiry = selectedExpiries.MCX[activeCommodityTab];
    const selectedComexExpiry = selectedExpiries.COMEX[activeCommodityTab];

    const exchangeKey = (activeCommodityTab === 'CRUDEOIL' || activeCommodityTab === 'NATURALGAS') ? 'NYMEX' : 'COMEX';
    const currentOkFuture = futuresData[`${exchangeKey}_${activeCommodityTab}_${selectedComexExpiry}`] || {};
    const currentMcxFuture = futuresData[`MCX_${activeCommodityTab}_${selectedMcxExpiry}`] || {};

    const mcxLtp = currentMcxFuture?.ltp;
    const comexLtp = currentOkFuture?.ltp;

    const mcxAtm = getAtmStrike(mcxLtp, STRIKE_STEPS[activeCommodityTab]?.MCX || 1000);
    const comexAtm = getAtmStrike(comexLtp, STRIKE_STEPS[activeCommodityTab]?.COMEX || 10);

    const mcxTableRef = useRef(null);
    const comexTableRef = useRef(null);

    const mcxChain = chainData[activeCommodityTab]?.[selectedMcxExpiry] || {};
    const comexChain = chainData[activeCommodityTab]?.[selectedComexExpiry] || {};

    const mcxStrikes = Object.keys(mcxChain).filter(strike => mcxChain[strike]?.MCX).sort((a, b) => parseFloat(a) - parseFloat(b));
    const comexStrikes = Object.keys(comexChain).filter(strike => comexChain[strike]?.[exchangeKey]).sort((a, b) => parseFloat(a) - parseFloat(b));

    const handleAlertClick = (alert) => {
        const { underlying, expiry } = alert; // expiry format "Feb 2026" or "26 Feb 2026"
        setActiveCommodityTab(underlying);

        // Helper to find matching expiry string
        // Alert expiry might be "Feb 2026", but select option might be "26 Feb 2026"
        // We look for partial match of Month and Year
        const parts = expiry.split(' ');
        const mon = parts.length === 3 ? parts[1] : parts[0];
        const yr = parts.length === 3 ? parts[2] : parts[1];

        const findMatch = (list) => list?.find(opt => opt.includes(mon) && opt.includes(yr));

        const mcxMatch = findMatch(expiries.MCX[underlying]);
        const comexMatch = findMatch(expiries.COMEX[underlying]);

        setSelectedExpiries(prev => ({
            MCX: { ...prev.MCX, [underlying]: mcxMatch || prev.MCX[underlying] },
            COMEX: { ...prev.COMEX, [underlying]: comexMatch || prev.COMEX[underlying] }
        }));
    };

    const getExpiryLtp = (exchange, commodity, index) => {
        const exchangeKey = exchange === 'COMEX' && (commodity === 'CRUDEOIL' || commodity === 'NATURALGAS') ? 'NYMEX' : exchange;
        const expiryList = expiries[`${exchange}_FUTURES`][commodity] || [];
        const expiry = expiryList[index];
        if (!expiry) return null;
        return futuresData[`${exchangeKey}_${commodity}_${expiry}`]?.ltp ?? null;
    };


    const renderExpiryCell = (exchange, commodity, index, isFutureTable = false, compareToIdx = null, align = "end") => {
        const exchangeKey = exchange === 'COMEX' && (commodity === 'CRUDEOIL' || commodity === 'NATURALGAS') ? 'NYMEX' : exchange;
        const expiryList = isFutureTable ? expiries[`${exchange}_FUTURES`][commodity] || [] : expiries[exchange][commodity] || [];
        const expiry = expiryList[index];
        if (!expiry) return <span className="text-slate-600 font-mono">-</span>;

        const futureKey = `${exchangeKey}_${commodity}_${expiry}`;
        const ltp = futuresData[futureKey]?.ltp;

        // diff badge vs compareToIdx
        let diffBadge = null;
        if (compareToIdx !== null && ltp) {
            const baseLtp = getExpiryLtp(exchange, commodity, compareToIdx);
            if (baseLtp) {
                const diffValues = futuresDiffMode === 'pct'
                    ? { val: ((ltp - baseLtp) / baseLtp) * 100, format: (v) => `${v.toFixed(3)}%` }
                    : { val: ltp - baseLtp, format: (v) => (exchange === 'MCX' ? `₹${v.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`) };

                const isPos = diffValues.val >= 0;

                diffBadge = (
                    <span className={`inline-flex items-center gap-0.5 font-mono text-[9px] font-bold px-1 py-px rounded mt-0.5 ${isPos ? 'text-emerald-400 bg-emerald-500/10' : 'text-red-400 bg-red-500/10'
                        }`}>
                        {isPos ? '▲' : '▼'} {diffValues.format(Math.abs(diffValues.val))}
                    </span>
                );
            }
        }

        return (
            <div className={`flex flex-col items-${align} justify-center w-full`}>
                <span className="text-[9px] text-slate-500 font-medium mb-0.5 leading-none">{expiry}</span>
                <div className="flex items-center gap-1.5">
                    <span className={`font-mono text-xs leading-tight ${ltp ? "text-emerald-400 font-bold" : "text-slate-500"}`}>
                        {ltp ? (exchange === 'MCX' ? `₹${ltp.toLocaleString('en-IN')}` : `$${ltp.toLocaleString()}`) : '-'}
                    </span>
                    {diffBadge}
                </div>
            </div>
        );
    };

    return (
        <>
            {/* Global Arbitrage Alerts Bar */}
            {/* Premium Arbitrage Alerts Panel */}
            {globalAlerts.length > 0 && (
                <div className="mb-6 bg-slate-900/50 border border-slate-700/50 rounded-xl overflow-hidden backdrop-blur-md shadow-2xl">
                    <div
                        className="px-4 py-2 bg-gradient-to-r from-red-500/10 to-transparent border-b border-red-500/20 flex justify-between items-center cursor-pointer hover:bg-white/5 transition-colors"
                        onClick={() => setIsAlertsExpanded(!isAlertsExpanded)}
                    >
                        <div className="flex items-center gap-2">
                            <div className={`transition-transform duration-200 ${isAlertsExpanded ? 'rotate-180' : ''}`}>
                                <ChevronDown size={16} className="text-red-400" />
                            </div>
                            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                            <span className="text-xs font-bold text-red-400 tracking-wider">ARBITRAGE OPPORTUNITIES DETECTED</span>
                            <span className="ml-2 bg-red-500/20 text-red-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-red-500/30">
                                {globalAlerts.length}
                            </span>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="text-[10px] text-slate-500 uppercase tracking-wider">
                                {isAlertsExpanded ? 'Collapse' : 'Expand'}
                            </span>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setGlobalAlerts([]);
                                }}
                                className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-300 transition-colors border-l border-slate-700 pl-4"
                            >
                                Dismiss All
                            </button>
                        </div>
                    </div>

                    {isAlertsExpanded && (
                        <div className="max-h-64 overflow-y-auto custom-scrollbar p-2 grid gap-2">
                            {globalAlerts.map((alert, idx) => {
                                const details = alert.details || {};
                                const hasDetails = !!details.mcx_strike;

                                return (
                                    <div
                                        key={idx}
                                        onClick={() => handleAlertClick(alert)}
                                        className="group relative bg-[#0f172a]/80 hover:bg-[#1e293b] border border-slate-800 hover:border-slate-600 rounded-lg p-3 transition-all duration-200 cursor-pointer flex items-center justify-between"
                                    >
                                        <div className="flex items-center gap-4">
                                            {/* Symbol Badge */}
                                            <div className="flex flex-col items-center justify-center w-12 h-12 rounded-lg bg-slate-800/50 border border-slate-700/50 group-hover:border-slate-600 transition-colors">
                                                <span className={`text-[10px] font-bold ${alert.underlying === 'GOLD' ? 'text-yellow-500' :
                                                    alert.underlying === 'SILVER' ? 'text-slate-300' :
                                                        alert.underlying === 'CRUDEOIL' ? 'text-black' : 'text-blue-400'
                                                    }`}>{alert.underlying && alert.underlying.substring(0, 3)}</span>
                                                <span className="text-[9px] text-slate-500">{alert.expiry && alert.expiry.split(' ')[0]}</span>
                                            </div>

                                            {/* Info */}
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm font-bold text-slate-200">{alert.underlying}</span>
                                                    <span className="text-[10px] px-1.5 rounded bg-slate-800 text-slate-400 border border-slate-700">{alert.expiry}</span>
                                                </div>

                                                {hasDetails ? (
                                                    <div className="text-xs text-slate-400 flex items-center gap-1">
                                                        <span className="text-slate-500">Strikes:</span>
                                                        <span className="font-mono text-slate-300">{details.mcx_strike}</span>
                                                        <span className="text-slate-600">/</span>
                                                        <span className="font-mono text-slate-300">{details.comex_strike}</span>
                                                    </div>
                                                ) : (
                                                    <div className="text-xs text-slate-400">{alert.msg}</div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Metrics */}
                                        {hasDetails && (
                                            <div className="flex items-center gap-6 mr-4">
                                                {/* Straddle Diff (Primary) */}
                                                <div className="flex flex-col items-end">
                                                    <span className="text-[9px] uppercase tracking-wider text-slate-500">Straddle Diff</span>
                                                    <span className={`text-sm font-bold font-mono ${details.diff_straddle > 15 ? 'text-red-400' : 'text-emerald-400'}`}>
                                                        {details.diff_straddle}%
                                                    </span>
                                                </div>

                                                <div className="w-px h-6 bg-slate-800"></div>

                                                {/* Individual Diffs */}
                                                <div className="flex flex-col gap-0.5">
                                                    <div className="flex items-center gap-2 text-[10px]">
                                                        <span className="text-slate-500 w-4">CE</span>
                                                        <span className={`${details.diff_ce > 15 ? 'text-orange-400' : 'text-slate-300'} font-mono`}>{details.diff_ce}%</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-[10px]">
                                                        <span className="text-slate-500 w-4">PE</span>
                                                        <span className={`${details.diff_pe > 15 ? 'text-orange-400' : 'text-slate-300'} font-mono`}>{details.diff_pe}%</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Action Arrow */}
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Unified Arbitrage Table */}
            <div className="flex flex-col flex-shrink-0 mb-6 bg-[#1e293b] rounded-xl border border-slate-700/50 shadow-xl overflow-hidden backdrop-blur-sm">

                {/* Header / Current Future Prices */}
                {/* Header built into table below */}

                {/* Unified Table */}
                <div className="flex-1 overflow-auto custom-scrollbar">
                    <table className="w-full text-sm border-collapse">
                        <thead className="sticky top-0 bg-[#0f172a] z-20 shadow-sm border-b border-slate-700">
                            <tr>
                                {/* MCX HEADER SECTION (Spans 8 Columns) */}
                                <th colSpan={8} className="py-3 px-4 text-left border-r border-slate-700/50 bg-[#1e293b]/50">
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-3">
                                            <div className="p-1.5 rounded-lg bg-indigo-500/10 text-indigo-400">
                                                <Activity size={16} />
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-bold text-slate-300 uppercase tracking-wider">MCX {activeCommodityTab}</span>
                                                    <div className="relative group">
                                                        <select
                                                            value={selectedMcxExpiry || ""}
                                                            onChange={(e) => setSelectedExpiries(prev => ({ ...prev, MCX: { ...prev.MCX, [activeCommodityTab]: e.target.value } }))}
                                                            className="appearance-none bg-slate-800/80 text-[10px] font-semibold text-emerald-400 font-mono rounded-md pl-2 pr-6 py-1 outline-none border border-slate-700 hover:border-indigo-500/50 focus:border-indigo-500 cursor-pointer"
                                                        >
                                                            {expiries.MCX[activeCommodityTab]?.map(opt => <option key={opt} value={opt} className="bg-slate-900 text-slate-300">{opt}</option>)}
                                                        </select>
                                                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-1 text-slate-500">
                                                            <ChevronDown size={12} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-base font-mono font-bold text-slate-200">
                                                ₹{mcxLtp ? mcxLtp.toLocaleString() : '-'}
                                            </div>
                                        </div>
                                    </div>
                                </th>

                                {/* ARBITRAGE HEADER SECTION (Spans 7 Columns) */}
                                <th colSpan={7} className="py-2 px-4 text-center border-r border-slate-700/50 bg-[#0f172a] text-slate-400 font-bold tracking-widest text-[10px] uppercase">
                                    <div className="flex items-center justify-center gap-2">
                                        <span>Arbitrage Diffs</span>
                                        {usdinrRate && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 font-mono">USDINR: ₹{usdinrRate.toFixed(2)}</span>}
                                    </div>
                                </th>

                                {/* COMEX HEADER SECTION (Spans 8 Columns) */}
                                <th colSpan={8} className="py-3 px-4 text-left bg-[#1e293b]/50">
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-3">
                                            <div className="p-1.5 rounded-lg bg-yellow-500/10 text-yellow-500">
                                                <Zap size={16} />
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-bold text-slate-300 uppercase tracking-wider">{exchangeKey} {activeCommodityTab}</span>
                                                    <div className="relative group">
                                                        <select
                                                            value={selectedComexExpiry || ""}
                                                            onChange={(e) => setSelectedExpiries(prev => ({ ...prev, COMEX: { ...prev.COMEX, [activeCommodityTab]: e.target.value } }))}
                                                            className="appearance-none bg-slate-800/80 text-[10px] font-semibold text-yellow-500 font-mono rounded-md pl-2 pr-6 py-1 outline-none border border-slate-700 hover:border-yellow-500/50 focus:border-yellow-500 cursor-pointer"
                                                        >
                                                            {expiries.COMEX[activeCommodityTab]?.map(opt => <option key={opt} value={opt} className="bg-slate-900 text-slate-300">{opt}</option>)}
                                                        </select>
                                                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-1 text-slate-500">
                                                            <ChevronDown size={12} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-base font-mono font-bold text-yellow-400">
                                                ${comexLtp ? comexLtp.toLocaleString() : '-'}
                                            </div>
                                        </div>
                                    </div>
                                </th>
                            </tr>

                            <tr className="text-[10px] uppercase text-slate-500 font-semibold bg-[#1e293b]">
                                {/* MCX SUB-HEADERS (8 Columns) */}
                                <th className="py-3 px-2 text-right text-orange-400">Straddle</th>
                                <th className="py-3 px-1 text-center">Time</th>
                                <th className="py-3 px-2 text-right font-bold text-slate-300">CE LTP</th>
                                <th className="py-3 px-2 pr-8 text-right">CE %</th>
                                <th className="py-3 px-2 text-left font-bold text-slate-300">PE LTP</th>
                                <th className="py-3 px-2 text-left">PE %</th>
                                <th className="py-3 px-1 text-center">Time</th>
                                <th className="py-3 px-4 text-center bg-[#0f172a] text-indigo-400 border-x border-slate-700/50 w-24">Strike</th>

                                {/* MIDDLE SUB-HEADERS (7 Columns) */}
                                <th className="py-3 px-2 text-center text-green-400 border-l border-slate-700/50">C CE Δ₹</th>
                                <th className="py-3 px-2 text-center text-green-400">Net CE Δ₹</th>
                                <th className="py-3 px-2 text-center text-green-400">M CE Δ₹</th>
                                <th className="py-3 px-2 text-center text-red-400">C PE Δ₹</th>
                                <th className="py-3 px-2 text-center text-red-400">Net PE Δ₹</th>
                                <th className="py-3 px-2 text-center text-red-400">M PE Δ₹</th>
                                <th className="py-3 px-2 text-center text-orange-400 border-r border-slate-700/50">Std Δ₹</th>

                                {/* COMEX SUB-HEADERS (8 Columns) */}
                                <th className="py-3 px-4 text-center bg-[#0f172a] text-yellow-500 border-x border-slate-700/50 w-24">Strike</th>
                                <th className="py-3 px-1 text-center">Time</th>
                                <th className="py-3 px-2 text-right font-bold text-slate-300">CE LTP</th>
                                <th className="py-3 px-2 pr-8 text-right">CE %</th>
                                <th className="py-3 px-2 text-left font-bold text-slate-300">PE LTP</th>
                                <th className="py-3 px-2 text-left">PE %</th>
                                <th className="py-3 px-1 text-center">Time</th>
                                <th className="py-3 px-2 text-left text-orange-400">Straddle</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/30">
                            {(() => {
                                if (!mcxAtm || !comexAtm) return (
                                    <tr><td colSpan={19} className="text-center py-8 text-slate-500">Waiting for market data...</td></tr>
                                );

                                const mcxStep = STRIKE_STEPS[activeCommodityTab]?.MCX || 1000;
                                const comexStep = STRIKE_STEPS[activeCommodityTab]?.COMEX || 10;

                                const rows = [];
                                for (let i = -3; i <= 3; i++) {
                                    const mcxStrike = mcxAtm + (i * mcxStep);
                                    const comexStrikeRaw = comexAtm + (i * comexStep);
                                    const comexStrike = comexStep < 1 ? parseFloat(comexStrikeRaw.toFixed(2)) : comexStrikeRaw;

                                    const mcxData = mcxChain[mcxStrike]?.MCX || {};
                                    const comexData = comexChain[comexStrike]?.[exchangeKey] || {};

                                    const mcxStraddle = calculateStraddle(mcxData.ce?.ltp, mcxData.pe?.ltp, mcxStrike);
                                    const comexStraddle = calculateStraddle(comexData.ce?.ltp, comexData.pe?.ltp, comexStrike);

                                    // Check for Midpoint status
                                    const isMidCe = comexData.ce?.is_mid;
                                    const isMidPe = comexData.pe?.is_mid;

                                    // --- INR Conversion Diffs ---
                                    const ratio = CONVERSION_RATIOS[activeCommodityTab] || { comex: 1, mcx: 1 };
                                    const isValid = (val) => val !== undefined && val !== null && val > 0;

                                    const mcxCeLtp = mcxData.ce?.ltp;
                                    const comexCeLtp = comexData.ce?.ltp;
                                    const mcxPeLtp = mcxData.pe?.ltp;
                                    const comexPeLtp = comexData.pe?.ltp;

                                    // INR-converted values (for % diff — alert threshold)
                                    const comexCeInr = (isValid(comexCeLtp) && usdinrRate) ? comexCeLtp * ratio.comex * usdinrRate : null;
                                    const mcxCeInr = isValid(mcxCeLtp) ? mcxCeLtp * ratio.mcx : null;
                                    const comexPeInr = (isValid(comexPeLtp) && usdinrRate) ? comexPeLtp * ratio.comex * usdinrRate : null;
                                    const mcxPeInr = isValid(mcxPeLtp) ? mcxPeLtp * ratio.mcx : null;

                                    // INR delta values using lot sizes
                                    const lots = LOT_SIZES[activeCommodityTab] || { comex: 1, mcx: 1 };
                                    const comexCeDeltaInr = (isValid(comexCeLtp) && usdinrRate) ? comexCeLtp * lots.comex * ratio.comex * usdinrRate : null;
                                    const mcxCeDeltaInr = isValid(mcxCeLtp) ? mcxCeLtp * lots.mcx * ratio.mcx : null;
                                    const comexPeDeltaInr = (isValid(comexPeLtp) && usdinrRate) ? comexPeLtp * lots.comex * ratio.comex * usdinrRate : null;
                                    const mcxPeDeltaInr = isValid(mcxPeLtp) ? mcxPeLtp * lots.mcx * ratio.mcx : null;

                                    // % Diff: abs(A-B) / avg(A,B) * 100
                                    const pctDiff = (a, b) => {
                                        if (!a || !b) return null;
                                        const avg = (a + b) / 2;
                                        if (avg === 0) return null;
                                        return (Math.abs(a - b) / avg * 100);
                                    };

                                    const diffCePct = pctDiff(comexCeInr, mcxCeInr);
                                    const diffPePct = pctDiff(comexPeInr, mcxPeInr);
                                    const diffCe = diffCePct !== null ? diffCePct.toFixed(2) : "-";
                                    const diffPe = diffPePct !== null ? diffPePct.toFixed(2) : "-";

                                    // Absolute INR delta using lot sizes: COMEX_delta_INR - MCX_delta_INR
                                    const deltaCe = (comexCeDeltaInr && mcxCeDeltaInr) ? (comexCeDeltaInr - mcxCeDeltaInr).toFixed(2) : null;
                                    const deltaPe = (comexPeDeltaInr && mcxPeDeltaInr) ? (comexPeDeltaInr - mcxPeDeltaInr).toFixed(2) : null;

                                    // Straddle
                                    const comexStraddleInr = (comexCeInr && comexPeInr) ? comexCeInr + comexPeInr : null;
                                    const mcxStraddleInr = (mcxCeInr && mcxPeInr) ? mcxCeInr + mcxPeInr : null;
                                    const diffStraddlePct = pctDiff(comexStraddleInr, mcxStraddleInr);
                                    const diffStraddle = diffStraddlePct !== null ? diffStraddlePct.toFixed(2) : "-";
                                    const comexStraddleDeltaInr = (comexCeDeltaInr && comexPeDeltaInr) ? comexCeDeltaInr + comexPeDeltaInr : null;
                                    const mcxStraddleDeltaInr = (mcxCeDeltaInr && mcxPeDeltaInr) ? mcxCeDeltaInr + mcxPeDeltaInr : null;
                                    const deltaStraddle = (comexStraddleDeltaInr && mcxStraddleDeltaInr) ? (comexStraddleDeltaInr - mcxStraddleDeltaInr).toFixed(2) : null;

                                    // Keep old ltp_change for display in MCX/COMEX columns
                                    const mcxCeChg = mcxData.ce?.ltp_change;
                                    const comexCeChg = comexData.ce?.ltp_change;
                                    const mcxPeChg = mcxData.pe?.ltp_change;
                                    const comexPeChg = comexData.pe?.ltp_change;

                                    // Highlight Logic
                                    const isAtm = i === 0;
                                    const isDiffAlert = (val) => parseFloat(val) > 15;

                                    rows.push(
                                        <tr key={i} className={`hover:bg-slate-700/20 transition-colors group ${isAtm ? 'bg-indigo-500/5' : ''}`}>
                                            {/* --- MCX DATA (8 Cols) --- */}
                                            <td className="py-3 px-2 text-right font-mono text-xs text-orange-400 font-bold">{mcxStraddle}%</td>
                                            <td className="py-3 px-1 text-center font-mono text-[10px] text-slate-300">{formatLtt(mcxData.ce?.ltt) || '-'}</td>
                                            <td className="py-3 px-2 text-right font-mono text-sm font-medium text-emerald-400">{mcxData.ce?.ltp || '-'}</td>
                                            <td className={`py-3 px-2 pr-8 text-right font-mono text-xs ${mcxCeChg >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                                                {mcxData.ce?.ltp_change ? `${parseFloat(mcxData.ce.ltp_change).toFixed(2)}%` : '-'}
                                            </td>
                                            <td className="py-3 px-2 text-left font-mono text-sm font-medium text-rose-400">{mcxData.pe?.ltp || '-'}</td>
                                            <td className={`py-3 px-2 text-left font-mono text-xs ${mcxPeChg >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                                                {mcxData.pe?.ltp_change ? `${parseFloat(mcxData.pe.ltp_change).toFixed(2)}%` : '-'}
                                            </td>
                                            <td className="py-3 px-1 text-center font-mono text-[10px] text-slate-300">{formatLtt(mcxData.pe?.ltt) || '-'}</td>

                                            {/* MCX Strike (Right) */}
                                            <td className="py-3 px-4 text-center font-mono font-bold text-slate-300 bg-slate-800/30 border-x border-slate-700/50 group-hover:bg-indigo-500/20 transition-colors">
                                                {mcxStrike}
                                            </td>


                                            {/* --- MIDDLE DIFFS (7 Cols) --- */}
                                            <td className={`py-3 px-2 text-center font-mono text-xs border-l border-slate-700/50 ${comexCeDeltaInr ? (comexCeDeltaInr >= 0 ? 'text-emerald-400' : 'text-rose-400') : 'text-slate-500'}`}>
                                                {comexCeDeltaInr ? `₹${parseFloat(comexCeDeltaInr).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-'}
                                            </td>
                                            <td className={`py-3 px-2 text-center font-mono text-xs ${deltaCe ? (parseFloat(deltaCe) >= 0 ? 'text-emerald-400' : 'text-rose-400') : 'text-slate-500'}`}>
                                                {deltaCe ? `₹${parseFloat(deltaCe).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-'}
                                            </td>
                                            <td className={`py-3 px-2 text-center font-mono text-xs ${mcxCeDeltaInr ? (mcxCeDeltaInr >= 0 ? 'text-emerald-400' : 'text-rose-400') : 'text-slate-500'}`}>
                                                {mcxCeDeltaInr ? `₹${parseFloat(mcxCeDeltaInr).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-'}
                                            </td>

                                            <td className={`py-3 px-2 text-center font-mono text-xs border-l border-slate-700/50 ${comexPeDeltaInr ? (comexPeDeltaInr >= 0 ? 'text-emerald-400' : 'text-rose-400') : 'text-slate-500'}`}>
                                                {comexPeDeltaInr ? `₹${parseFloat(comexPeDeltaInr).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-'}
                                            </td>
                                            <td className={`py-3 px-2 text-center font-mono text-xs ${deltaPe ? (parseFloat(deltaPe) >= 0 ? 'text-emerald-400' : 'text-rose-400') : 'text-slate-500'}`}>
                                                {deltaPe ? `₹${parseFloat(deltaPe).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-'}
                                            </td>
                                            <td className={`py-3 px-2 text-center font-mono text-xs ${mcxPeDeltaInr ? (mcxPeDeltaInr >= 0 ? 'text-emerald-400' : 'text-rose-400') : 'text-slate-500'}`}>
                                                {mcxPeDeltaInr ? `₹${parseFloat(mcxPeDeltaInr).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-'}
                                            </td>

                                            <td className={`py-3 px-2 text-center font-mono text-xs border-r border-slate-700/50 ${deltaStraddle ? (parseFloat(deltaStraddle) >= 0 ? 'text-emerald-400' : 'text-rose-400') : 'text-slate-500'}`}>
                                                {deltaStraddle ? `₹${parseFloat(deltaStraddle).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-'}
                                            </td>

                                            {/* --- COMEX DATA (8 Cols) --- */}
                                            {/* COMEX Strike (Left) */}
                                            <td className="py-3 px-4 text-center font-mono font-bold text-slate-300 bg-slate-800/30 border-x border-slate-700/50 group-hover:bg-yellow-500/20 transition-colors">
                                                {comexStrike}
                                            </td>

                                            <td className="py-3 px-1 text-center font-mono text-[10px] text-slate-300">{formatLtt(comexData.ce?.ltt) || '-'}</td>
                                            <td className="py-3 px-2 text-right font-mono text-sm font-medium text-emerald-400">
                                                <div className="flex items-center justify-end gap-1">
                                                    {comexData.ce?.ltp || '-'}
                                                    {isMidCe && <span className="text-[9px] px-1 rounded bg-yellow-500/20 text-yellow-500 font-bold">Mid</span>}
                                                </div>
                                            </td>
                                            <td className={`py-3 px-2 pr-8 text-right font-mono text-xs ${comexCeChg >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                                                {comexData.ce?.ltp_change ? `${parseFloat(comexData.ce.ltp_change).toFixed(2)}%` : '-'}
                                            </td>
                                            <td className="py-3 px-2 text-left font-mono text-sm font-medium text-rose-400">
                                                <div className="flex items-center gap-1">
                                                    {isMidPe && <span className="text-[9px] px-1 rounded bg-yellow-500/20 text-yellow-500 font-bold">Mid</span>}
                                                    {comexData.pe?.ltp || '-'}
                                                </div>
                                            </td>
                                            <td className={`py-3 px-2 text-left font-mono text-xs ${comexPeChg >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                                                {comexData.pe?.ltp_change ? `${parseFloat(comexData.pe.ltp_change).toFixed(2)}%` : '-'}
                                            </td>
                                            <td className="py-3 px-1 text-center font-mono text-[10px] text-slate-300">{formatLtt(comexData.pe?.ltt) || '-'}</td>
                                            <td className="py-3 px-2 text-left font-mono text-xs text-orange-400 font-bold">{comexStraddle}%</td>
                                        </tr>
                                    );
                                }
                                return rows;
                            })()}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Futures Term Structure Table */}
            <div className="flex flex-col shrink-0 mb-6 bg-[#1e293b] rounded-xl border border-slate-700/50 shadow-xl overflow-hidden backdrop-blur-sm">
                <div className="px-3 py-2 bg-[#0f172a] border-b border-slate-700/50 flex items-center justify-between">
                    <h3 className="text-xs font-bold text-slate-200 flex items-center gap-2">
                        <Activity size={14} className="text-indigo-400" />
                        Futures Term Structure
                    </h3>
                    <div className="flex items-center bg-slate-800/80 p-0.5 rounded-lg border border-slate-700/80">
                        <button
                            onClick={() => setFuturesDiffMode('pct')}
                            className={`px-3 py-1 rounded-md text-[10px] font-bold tracking-wider transition-all duration-200 ${futuresDiffMode === 'pct' ? 'bg-indigo-500 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'}`}
                        >
                            % DIFF
                        </button>
                        <button
                            onClick={() => setFuturesDiffMode('inr')}
                            className={`px-3 py-1 rounded-md text-[10px] font-bold tracking-wider transition-all duration-200 ${futuresDiffMode === 'inr' ? 'bg-indigo-500 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'}`}
                        >
                            PRICE DIFF
                        </button>
                    </div>
                </div>
                <div className="overflow-x-auto custom-scrollbar">
                    <table className="w-full text-xs border-collapse">
                        <thead className="bg-[#1e293b]/80 border-b border-slate-700/50">
                            <tr>
                                <th className="py-1.5 px-3 text-left text-[9px] uppercase font-bold text-slate-400 tracking-wider w-32 border-r border-slate-700/30">Asset</th>
                                <th className="py-1.5 px-3 text-center text-[9px] uppercase font-bold text-slate-400 tracking-wider w-24 border-r border-slate-700/30">Exchange</th>
                                <th className="py-1.5 px-3 text-center text-[9px] uppercase font-bold text-slate-400 tracking-wider border-r border-slate-700/30">Current Expiry</th>
                                <th className="py-1.5 px-3 text-center text-[9px] uppercase font-bold text-slate-400 tracking-wider border-r border-slate-700/30">Next Expiry</th>
                                <th className="py-1.5 px-3 text-center text-[9px] uppercase font-bold text-slate-400 tracking-wider">Next+1 Expiry</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/30 bg-[#0f172a]/20">
                            {['GOLD', 'SILVER', 'CRUDEOIL', 'NATURALGAS'].map((commodity, idx) => (
                                <React.Fragment key={commodity}>
                                    <tr className={`hover:bg-slate-700/10 transition-colors ${idx % 2 === 0 ? 'bg-transparent' : 'bg-[#1e293b]/10'}`}>
                                        <td className="py-1.5 px-3 text-[11px] font-bold text-slate-200 border-r border-slate-700/30 align-middle" rowSpan={2}>
                                            <div className="flex items-center gap-2">
                                                <div className={`flex items-center justify-center w-5 h-5 rounded bg-slate-800/80 border border-slate-600/50 shadow-inner`}>
                                                    <div className={`w-2 h-2 rounded-full ${commodity === 'GOLD' ? 'bg-yellow-500 shadow-[0_0_6px_rgba(234,179,8,0.5)]' : commodity === 'SILVER' ? 'bg-slate-300 shadow-[0_0_6px_rgba(203,213,225,0.5)]' : commodity === 'CRUDEOIL' ? 'bg-black border border-slate-500' : 'bg-blue-400 shadow-[0_0_6px_rgba(96,165,250,0.5)]'}`}></div>
                                                </div>
                                                <span className="tracking-wide">{commodity}</span>
                                            </div>
                                        </td>
                                        <td className="py-1.5 px-3 text-center align-middle border-r border-slate-700/30">
                                            <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded bg-indigo-500/10 border border-indigo-500/20 text-[9px] font-bold text-indigo-400 uppercase tracking-widest min-w-[50px]">MCX</span>
                                        </td>
                                        <td className="py-1.5 px-3 bg-indigo-500/5 border-r border-slate-700/30">{renderExpiryCell('MCX', commodity, 0, true, null, "center")}</td>
                                        <td className="py-1.5 px-3 bg-indigo-500/5 border-r border-slate-700/30">{renderExpiryCell('MCX', commodity, 1, true, 0, "center")}</td>
                                        <td className="py-1.5 px-3 bg-indigo-500/5">{renderExpiryCell('MCX', commodity, 2, true, 0, "center")}</td>
                                    </tr>
                                    <tr className={`hover:bg-slate-700/10 transition-colors border-b-2 border-slate-700/50 ${idx % 2 === 0 ? 'bg-transparent' : 'bg-[#1e293b]/10'}`}>
                                        <td className="py-1.5 px-3 text-center align-middle border-r border-slate-700/30">
                                            <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded bg-yellow-500/10 border border-yellow-500/20 text-[9px] font-bold text-yellow-500 uppercase tracking-widest min-w-[50px]">
                                                {(commodity === 'CRUDEOIL' || commodity === 'NATURALGAS') ? 'NYMEX' : 'COMEX'}
                                            </span>
                                        </td>
                                        <td className="py-1.5 px-3 bg-yellow-500/5 border-r border-slate-700/30">{renderExpiryCell('COMEX', commodity, 0, true, null, "center")}</td>
                                        <td className="py-1.5 px-3 bg-yellow-500/5 border-r border-slate-700/30">{renderExpiryCell('COMEX', commodity, 1, true, 0, "center")}</td>
                                        <td className="py-1.5 px-3 bg-yellow-500/5">{renderExpiryCell('COMEX', commodity, 2, true, 0, "center")}</td>
                                    </tr>
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
};

export default ArbitrageView;
